/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { loadFromStorage } from "./Auth";
import {
  analyseSquad,
  generateOppositionReport,
  generatePlayerDevelopment,
  buildTrainingSession,
  generateVideoNarrative,
  runPatternEngine,
  computeSeasonPatterns,
  AIResponse,
  AI_CSS,
} from "./AIEngine";

/* ── Direct streaming call for team talks ── */
async function callClaudeTeamTalk(systemPrompt, userMessage, onChunk) {
  const res = await fetch("/api/claude", {
    method: "POST",
    headers: {"Content-Type":"application/json"},
    body: JSON.stringify({
      system: systemPrompt,
      messages: [{ role: "user", content: userMessage }],
      max_tokens: 600,
      stream: true,
    }),
  });
  if (!res.ok) throw new Error(`Server error ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    for (const line of chunk.split("\n")) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (data === "[DONE]") continue;
      try {
        const parsed = JSON.parse(data);
        const delta = parsed?.delta?.text || parsed?.content?.[0]?.text || "";
        if (delta) { full += delta; onChunk(full); }
      } catch {}
    }
  }
  return full;
}

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — AI ANALYST
   Five AI-powered features in one screen
   Squad Analyst · Opposition Scout · Player Dev
   Session Builder · Video Narrative
═══════════════════════════════════════════════════════════ */

const AA_CSS = `
.aa-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* HERO */
.aa-hero { background:linear-gradient(135deg,var(--ink) 0%,var(--teal2) 100%); border-bottom:3px solid var(--mustard); padding:1.8rem; position:relative; overflow:hidden; }
.aa-hero::before { content:'AI'; position:absolute; right:-20px; top:50%; transform:translateY(-50%); font-family:'Bebas Neue',sans-serif; font-size:12rem; letter-spacing:8px; color:rgba(255,255,255,0.03); pointer-events:none; }
.aa-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.aa-hero-title { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px; color:var(--white); line-height:1; }
.aa-hero-sub { font-size:0.75rem; color:rgba(255,255,255,0.55); font-style:italic; font-weight:300; margin-top:0.25rem; max-width:520px; line-height:1.6; }

/* TABS */
.aa-tabs { display:flex; background:var(--white); border-bottom:2px solid var(--rule); overflow-x:auto; }
.aa-tab { padding:0.85rem 1.2rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; color:var(--muted); border-bottom:3px solid transparent; margin-bottom:-2px; white-space:nowrap; transition:all 0.2s; display:flex; align-items:center; gap:0.4rem; }
.aa-tab:hover { color:var(--teal); }
.aa-tab.on { color:var(--teal); border-bottom-color:var(--teal); }
.aa-tab-icon { font-size:0.9rem; }

/* CONTENT */
.aa-content { padding:1.5rem 1.8rem; max-width:900px; }
@media(max-width:700px) {
  .aa-content { padding:0.85rem; }
  .aa-hero { padding:1.2rem; }
  .aa-tabs { font-size:0.62rem; }
  .aa-tab { padding:0.7rem 0.7rem; letter-spacing:0.5px; }
  .aa-player-attrs { flex-wrap:wrap; }
  .aa-video-row { flex-wrap:wrap; }
}

/* Panel */
.aa-panel { background:var(--white); border:1.5px solid var(--rule); margin-bottom:1.2rem; }
.aa-panel-head { padding:0.9rem 1.1rem; border-bottom:1.5px solid var(--rule); display:flex; align-items:center; justify-content:space-between; }
.aa-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:2.5px; color:var(--ink); }
.aa-panel-body { padding:1.1rem; }

/* Squad summary chips */
.aa-squad-chip { font-size:0.62rem; font-weight:800; padding:0.2rem 0.55rem; letter-spacing:1.5px; text-transform:uppercase; border:1.5px solid var(--rule); color:var(--muted); margin-right:0.35rem; margin-bottom:0.35rem; display:inline-block; }
.aa-squad-chip.gk { border-color:#f59e0b; color:#b45309; }
.aa-squad-chip.def { border-color:var(--darkred); color:var(--darkred); }
.aa-squad-chip.mid { border-color:var(--teal); color:var(--teal); }
.aa-squad-chip.att { border-color:#16a34a; color:#15803d; }

/* Form elements */
.aa-label { display:block; font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.35rem; margin-top:0.75rem; }
.aa-label:first-child { margin-top:0; }
.aa-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; }
.aa-input:focus { border-bottom-color:var(--teal); }
.aa-textarea { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:400; outline:none; resize:vertical; min-height:90px; font-style:italic; }
.aa-textarea:focus { border-bottom-color:var(--teal); }
.aa-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.85rem; font-weight:500; outline:none; cursor:pointer; }

/* Player selector */
.aa-player-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.62rem 0.85rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; outline:none; cursor:pointer; }
.aa-player-card { background:var(--teal4); border:1.5px solid var(--teal); border-left:4px solid var(--teal); padding:0.85rem; margin:0.75rem 0; display:flex; align-items:flex-start; gap:0.75rem; }
.aa-player-av { width:40px; height:40px; border-radius:50%; border:2px solid var(--ink); display:flex; align-items:center; justify-content:center; font-size:0.75rem; font-weight:800; color:var(--white); flex-shrink:0; }
.aa-player-name { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:1.5px; color:var(--ink); }
.aa-player-meta { font-size:0.65rem; color:var(--muted); font-weight:300; margin-top:0.1rem; }
.aa-player-attrs { display:flex; gap:0.3rem; margin-top:0.5rem; flex-wrap:wrap; }
.aa-player-attr { background:var(--white); border:1px solid var(--rule2); padding:0.12rem 0.38rem; font-family:'Bebas Neue',sans-serif; font-size:0.78rem; letter-spacing:1px; }

/* Empty state */
.aa-empty { text-align:center; padding:3rem 1rem; }
.aa-empty-icon { font-size:2.5rem; margin-bottom:0.75rem; }
.aa-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.3rem; letter-spacing:2px; margin-bottom:0.4rem; }
.aa-empty-sub { font-size:0.78rem; color:var(--muted); font-style:italic; font-weight:300; max-width:380px; margin:0 auto; line-height:1.7; }

/* Video data summary */
.aa-video-row { display:flex; gap:0.5rem; flex-wrap:wrap; margin-bottom:0.75rem; }
.aa-video-chip { background:var(--cream2); border:1.5px solid var(--rule); padding:0.38rem 0.75rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:700; color:var(--ink); }
.aa-video-chip strong { color:var(--teal); }
`;

function posGroupColor(group) {
  if(group==="gk") return "#f59e0b";
  if(group==="def") return "var(--darkred)";
  if(group==="mid") return "var(--teal)";
  return "#16a34a";
}

function ini(name) {
  return (name||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
}

/* ── Shared AI Output Panel ── */
function AIOutputPanel({ title, output, streaming, error, onClear }) {
  const ref = useRef(null);
  useEffect(()=>{ if(ref.current && streaming) ref.current.scrollTop = ref.current.scrollHeight; },[output]);

  if (!output && !error) return null;

  return (
    <div className="aa-panel" style={{borderTop:"3px solid var(--teal)"}}>
      <div className="aa-panel-head">
        <div className="aa-panel-title">{title} <span className="ai-badge">Claude AI</span></div>
        {!streaming && <button onClick={onClear} style={{background:"transparent",border:"1px solid var(--rule)",color:"var(--muted)",padding:"0.2rem 0.6rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:700,cursor:"pointer"}}>Clear</button>}
      </div>
      <div className="aa-panel-body" ref={ref} style={{maxHeight:600,overflowY:"auto"}}>
        {error ? (
          <div style={{color:"var(--darkred)",fontSize:"0.82rem",fontStyle:"italic"}}>{error}</div>
        ) : (
          <AIResponse text={output} streaming={streaming}/>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════
   PATTERN ENGINE TAB COMPONENT
═══════════════════════════════════════════════════════════ */
function PatternEngineTab({ patternOutput, patternStreaming, patternError, patternData, onRun, squad }) {
  const fixtures      = loadFromStorage("fx_fixtures", []) || [];
  const analyses      = loadFromStorage("va_analyses", []) || [];
  const playerRatings = loadFromStorage("player_match_ratings", []) || [];

  const played     = fixtures.filter(f => f.played);
  const hasEnough  = played.length >= 3;
  const dataPoints = [
    { label:"Matches played",     val:played.length,       min:3,  met:played.length>=3 },
    { label:"Match analyses",     val:analyses.length,     min:3,  met:analyses.length>=3 },
    { label:"Player ratings",     val:playerRatings.length,min:2,  met:playerRatings.length>=2 },
    { label:"Squad players",      val:squad.length,        min:5,  met:squad.length>=5 },
  ];

  const statBox = (label, val, sub, color="var(--teal)") => (
    <div style={{background:"var(--cream2)",border:"1.5px solid var(--rule)",padding:"0.75rem 0.85rem",textAlign:"center"}}>
      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.6rem",letterSpacing:"2px",color,lineHeight:1}}>{val}</div>
      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginTop:"0.1rem"}}>{label}</div>
      {sub&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.1rem"}}>{sub}</div>}
    </div>
  );

  const p = patternData;

  return (
    <div>
      {/* Hero */}
      <div style={{background:"var(--ink)",borderLeft:"4px solid var(--mustard)",padding:"1rem 1.2rem",marginBottom:"1.2rem"}}>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"2.5px",color:"var(--mustard)",marginBottom:"0.3rem"}}>
          Pattern Engine
        </div>
        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"rgba(255,255,255,0.6)",fontWeight:300,lineHeight:1.7}}>
          Reads across your entire season — results, press data, goal timing, player ratings — and identifies the patterns that explain your performance. Not a summary. An analysis.
        </div>
      </div>

      {/* Data readiness */}
      <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",marginBottom:"1.2rem"}}>
        <div style={{padding:"0.65rem 1rem",borderBottom:"1.5px solid var(--rule)",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.78rem",letterSpacing:"2px",color:"var(--ink)"}}>
          Data Available
        </div>
        <div style={{padding:"0.75rem 1rem",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:"0.5rem"}}>
          {dataPoints.map((d,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:"0.5rem",padding:"0.35rem 0"}}>
              <div style={{width:18,height:18,borderRadius:"50%",background:d.met?"#16a34a":"var(--rule)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{fontSize:"0.55rem",color:d.met?"white":"var(--muted)",fontWeight:800}}>{d.met?"✓":d.val}</span>
              </div>
              <div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",fontWeight:700,color:d.met?"var(--ink)":"var(--muted)"}}>{d.label}</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--muted)"}}>{d.val} {!d.met&&`(need ${d.min})`}</div>
              </div>
            </div>
          ))}
        </div>
        {!hasEnough&&(
          <div style={{padding:"0.65rem 1rem",borderTop:"1px solid var(--rule2)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic"}}>
            Log at least 3 match results in Fixtures to unlock the pattern engine. The more data you add, the more accurate the patterns.
          </div>
        )}
      </div>

      {/* Pre-analysis stats (computed immediately without AI) */}
      {hasEnough && !patternOutput && !patternStreaming && (()=>{
        const pl = played;
        const w = pl.filter(f=>f.goalsFor>f.goalsAgainst).length;
        const d = pl.filter(f=>f.goalsFor===f.goalsAgainst).length;
        const l = pl.filter(f=>f.goalsFor<f.goalsAgainst).length;
        const gf = pl.reduce((s,f)=>s+(f.goalsFor||0),0);
        const ga = pl.reduce((s,f)=>s+(f.goalsAgainst||0),0);
        const cs = pl.filter(f=>f.goalsAgainst===0).length;
        const home = pl.filter(f=>f.venue==="home");
        const away = pl.filter(f=>f.venue==="away");
        return(
          <div style={{marginBottom:"1.2rem"}}>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>Season at a Glance</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(100px,1fr))",gap:"0.5rem",marginBottom:"0.75rem"}}>
              {statBox("Record",`W${w} D${d} L${l}`)}
              {statBox("Goals For",`${gf}`,`${(gf/pl.length).toFixed(1)}/game`)}
              {statBox("Goals Against",`${ga}`,`${(ga/pl.length).toFixed(1)}/game`,ga<gf?"var(--teal)":"var(--darkred)")}
              {statBox("Clean Sheets",`${cs}`,`${Math.round(cs/pl.length*100)}%`)}
              {home.length>0&&statBox("Home W%",`${Math.round(home.filter(f=>f.goalsFor>f.goalsAgainst).length/home.length*100)}%`,`${home.length} games`)}
              {away.length>0&&statBox("Away W%",`${Math.round(away.filter(f=>f.goalsFor>f.goalsAgainst).length/away.length*100)}%`,`${away.length} games`)}
            </div>
          </div>
        );
      })()}

      {/* Run button */}
      {hasEnough&&(
        <button onClick={onRun} disabled={patternStreaming}
          style={{width:"100%",padding:"1rem",background:patternStreaming?"var(--muted)":"var(--ink)",color:patternStreaming?"rgba(255,255,255,0.5)":"var(--mustard)",border:"2px solid var(--mustard)",fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"3px",cursor:patternStreaming?"default":"pointer",marginBottom:"1.2rem",transition:"all 0.2s"}}>
          {patternStreaming?"Analysing your season...":"📈 Run Pattern Analysis →"}
        </button>
      )}

      {patternError&&(
        <div style={{background:"rgba(139,42,42,0.1)",border:"1.5px solid var(--darkred)",borderLeft:"4px solid var(--darkred)",padding:"0.85rem 1rem",marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--darkred)"}}>
          {patternError}
        </div>
      )}

      {/* Results */}
      {(patternOutput||patternStreaming)&&(
        <>
          {/* Raw stats grid — shown once analysis complete */}
          {p&&!patternStreaming&&(
            <div style={{marginBottom:"1.2rem"}}>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>Supporting Data</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:"0.5rem"}}>
                {[
                  p.avgPress&&{l:"Avg Press Eff.",v:`${p.avgPress}%`},
                  p.avgPressWins&&{l:"Press in Wins",v:`${p.avgPressWins}%`,c:"#16a34a"},
                  p.avgPressLosses&&{l:"Press in Losses",v:`${p.avgPressLosses}%`,c:"var(--darkred)"},
                  p.scoringWindow&&{l:"Best Scoring Window",v:p.scoringWindow.label},
                  p.concernWindow&&p.concernWindow.concerns>0&&{l:"Concern Window",v:p.concernWindow.label,c:"var(--darkred)"},
                  p.setpieceGoalPct!==null&&{l:"Set Piece Goal %",v:`${p.setpieceGoalPct}%`},
                  p.avgPossWins&&{l:"Possession in Wins",v:`${p.avgPossWins}%`,c:"#16a34a"},
                  p.avgPossLoss&&{l:"Possession in Losses",v:`${p.avgPossLoss}%`,c:"var(--darkred)"},
                ].filter(Boolean).map((item,i)=>(
                  <div key={i} style={{background:"var(--cream2)",border:"1px solid var(--rule)",padding:"0.6rem 0.75rem",textAlign:"center"}}>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"1px",color:item.c||"var(--teal)",lineHeight:1}}>{item.v}</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.55rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginTop:"0.1rem"}}>{item.l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Claude analysis */}
          <div style={{background:"var(--white)",border:"1.5px solid var(--rule)"}}>
            <div style={{padding:"0.75rem 1rem",borderBottom:"1.5px solid var(--rule)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.85rem",letterSpacing:"2px",color:"var(--ink)"}}>Season Pattern Findings</div>
              <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
                {patternStreaming&&<div style={{width:8,height:8,borderRadius:"50%",background:"var(--teal)",animation:"pulse 1s infinite"}}/>}
                <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"var(--teal)",color:"var(--white)",padding:"0.1rem 0.4rem"}}>Pattern Engine</span>
              </div>
            </div>
            <div style={{padding:"1.1rem"}}>
              <AIResponse text={patternOutput} streaming={patternStreaming}/>
            </div>
          </div>

          {/* Re-run */}
          {!patternStreaming&&(
            <button onClick={onRun} style={{marginTop:"0.75rem",padding:"0.5rem 1rem",background:"transparent",border:"1.5px solid var(--rule)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:"var(--muted)",cursor:"pointer"}}>
              ↺ Re-run Analysis
            </button>
          )}
        </>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </div>
  );
}

export default function AIAnalyst({ squad = [], onNavigate = null }) {
  const [activeTab, setActiveTab] = useState("squad");

  // Squad Analyst state
  const [squadQ, setSquadQ] = useState("");
  const [squadOutput, setSquadOutput] = useState("");
  const [squadStreaming, setSquadStreaming] = useState(false);
  const [squadError, setSquadError] = useState("");

  // Opposition Scout state
  const [oppName, setOppName] = useState("");
  const [oppStyle, setOppStyle] = useState("High Press");
  const [oppForm, setOppForm] = useState("4-3-3");
  const [oppThreats, setOppThreats] = useState("");
  const [oppOutput, setOppOutput] = useState("");
  const [oppStreaming, setOppStreaming] = useState(false);
  const [oppError, setOppError] = useState("");

  // Player Dev state
  const [selPlayerId, setSelPlayerId] = useState("");
  const [playerQ, setPlayerQ] = useState("");
  const [playerOutput, setPlayerOutput] = useState("");
  const [playerStreaming, setPlayerStreaming] = useState(false);
  const [playerError, setPlayerError] = useState("");

  // Session Builder state
  const [sessionBrief, setSessionBrief] = useState("");
  const [sessionOutput, setSessionOutput] = useState("");
  const [sessionStreaming, setSessionStreaming] = useState(false);
  const [sessionError, setSessionError] = useState("");

  // Video Narrative state
  const [videoOutput, setVideoOutput] = useState("");
  const [videoStreaming, setVideoStreaming] = useState(false);
  const [videoError, setVideoError] = useState("");
  const savedAnalyses = loadFromStorage("va_analyses", []);
  const [selAnalysis, setSelAnalysis] = useState(0);

  // Team Talk state
  const [talkType, setTalkType]     = useState("pre");    // pre | halftime | post
  const [talkTone, setTalkTone]     = useState("motivational"); // motivational | calm | aggressive | tactical | honest
  const [talkOpp, setTalkOpp]       = useState("");
  const [talkScore, setTalkScore]   = useState("");        // for halftime/post: "1-0", "0-2" etc
  const [talkContext, setTalkContext] = useState("");       // anything extra the manager wants to address
  const [talkOutput, setTalkOutput] = useState("");
  const [talkStreaming, setTalkStreaming] = useState(false);
  const [talkError, setTalkError]   = useState("");

  // Pattern Engine state
  const [patternOutput, setPatternOutput]   = useState("");
  const [patternStreaming, setPatternStreaming] = useState(false);
  const [patternError, setPatternError]     = useState("");
  const [patternData, setPatternData]       = useState(null);

  const selPlayer = squad.find(p => String(p.id) === selPlayerId);
  const latestAnalysis = savedAnalyses[selAnalysis] || null;

  /* ── PATTERN ENGINE ── */
  const runPatterns = async () => {
    setPatternStreaming(true); setPatternOutput(""); setPatternError(""); setPatternData(null);
    try {
      const fixtures      = loadFromStorage("fx_fixtures", []) || [];
      const analyses      = loadFromStorage("va_analyses", []) || [];
      const playerRatings = loadFromStorage("player_match_ratings", []) || [];
      const clubName      = loadFromStorage("fx_club_name", "My Club") || "My Club";
      const profile       = loadFromStorage("profile_pending", {}) || {};

      const { patterns, analysis } = await runPatternEngine(
        fixtures, analyses, playerRatings, squad, clubName, profile,
        (text) => setPatternOutput(text)
      );
      setPatternData(patterns);
    } catch(e) {
      setPatternError(e.message);
    }
    setPatternStreaming(false);
  };

  /* ── SQUAD ANALYST ── */
  const runSquadAnalysis = async () => {
    if (!squadQ.trim() || squad.length === 0) return;
    setSquadStreaming(true); setSquadOutput(""); setSquadError("");
    try {
      await analyseSquad(squad, squadQ, (text) => setSquadOutput(text));
    } catch(e) {
      setSquadError(`AI Error: ${e.message}. Check your connection and try again.`);
    }
    setSquadStreaming(false);
  };

  /* ── OPPOSITION SCOUT ── */
  const runOppositionReport = async () => {
    if (!oppName.trim()) return;
    setOppStreaming(true); setOppOutput(""); setOppError("");
    const threats = oppThreats.split(",").map(t=>t.trim()).filter(Boolean);
    try {
      await generateOppositionReport(oppName, oppStyle, oppForm, threats, squad, (text) => setOppOutput(text));
    } catch(e) {
      setOppError(`AI Error: ${e.message}`);
    }
    setOppStreaming(false);
  };

  /* ── PLAYER DEV ── */
  const runPlayerDev = async () => {
    if (!selPlayer) return;
    setPlayerStreaming(true); setPlayerOutput(""); setPlayerError("");
    try {
      await generatePlayerDevelopment(selPlayer, playerQ, (text) => setPlayerOutput(text));
    } catch(e) {
      setPlayerError(`AI Error: ${e.message}`);
    }
    setPlayerStreaming(false);
  };

  /* ── SESSION BUILDER ── */
  const runSessionBuilder = async () => {
    if (!sessionBrief.trim()) return;
    setSessionStreaming(true); setSessionOutput(""); setSessionError("");
    const formation = loadFromStorage("my_formation", "4-3-3") || "4-3-3";
    try {
      await buildTrainingSession(sessionBrief, squad, formation, (text) => setSessionOutput(text));
    } catch(e) {
      setSessionError(`AI Error: ${e.message}`);
    }
    setSessionStreaming(false);
  };

  /* ── VIDEO NARRATIVE ── */
  const runVideoNarrative = async () => {
    if (!latestAnalysis) return;
    setVideoStreaming(true); setVideoOutput(""); setVideoError("");
    try {
      await generateVideoNarrative(latestAnalysis, latestAnalysis.matchData || {}, (text) => setVideoOutput(text));
    } catch(e) {
      setVideoError(`AI Error: ${e.message}`);
    }
    setVideoStreaming(false);
  };

  const runTeamTalk = async () => {
    setTalkStreaming(true); setTalkOutput(""); setTalkError("");
    try {
      // Pull real data from storage
      const fixtures    = loadFromStorage("fx_fixtures", []) || [];
      const allRatings  = loadFromStorage("player_match_ratings", []) || [];
      const boardState  = loadFromStorage("tactics_board_state", null);
      const profile     = loadFromStorage("profile_pending", {}) || {};
      const clubName    = loadFromStorage("fx_club_name", "My Club") || "My Club";

      // Recent form — last 5 played results
      const played = fixtures.filter(f=>f.played).sort((a,b)=>new Date(b.date)-new Date(a.date)).slice(0,5);
      const formStr = played.length > 0
        ? played.map(f=>{
            const r = f.goalsFor > f.goalsAgainst ? "W" : f.goalsFor < f.goalsAgainst ? "L" : "D";
            return `${r} ${f.goalsFor}-${f.goalsAgainst} vs ${f.opponent}`;
          }).join(", ")
        : "No results logged yet";

      // Squad form — top performers from last match
      const lastMatch = allRatings[0];
      const topPerformers = lastMatch
        ? Object.entries(lastMatch.ratings||{})
            .map(([pid, rating]) => ({player: squad.find(p=>String(p.id)===pid), rating}))
            .filter(x=>x.player && x.rating >= 7)
            .sort((a,b)=>b.rating-a.rating)
            .slice(0,3)
            .map(x=>`${x.player.name} (${x.rating}/10)`)
            .join(", ")
        : "";

      const lowPerformers = lastMatch
        ? Object.entries(lastMatch.ratings||{})
            .map(([pid, rating]) => ({player: squad.find(p=>String(p.id)===pid), rating}))
            .filter(x=>x.player && x.rating <= 5)
            .sort((a,b)=>a.rating-b.rating)
            .slice(0,2)
            .map(x=>`${x.player.name} (${x.rating}/10)`)
            .join(", ")
        : "";

      // Formation/system
      const formation   = boardState?.formation || profile?.formation || "4-3-3";
      const systemName  = boardState?.currentSystem?.name || "";
      const principles  = boardState?.currentSystem?.principles?.slice(0,3).join("; ") || "";

      // Squad size for context
      const squadSize   = squad.length;
      const captainName = squad[0]?.name || "";

      const typeLabel   = talkType === "pre" ? "Pre-Match" : talkType === "halftime" ? "Half-Time" : "Post-Match";
      const toneLabel   = {
        motivational: "passionate and motivational — raise energy and belief",
        calm:         "calm, clear and composed — focus and clarity",
        aggressive:   "direct and confrontational — demand more",
        tactical:     "tactical and precise — specific instructions",
        honest:       "honest and direct — acknowledge issues but drive improvement",
      }[talkTone];

      const systemPrompt = `You are a football manager writing a genuine team talk. You speak directly to your players — not about them. Write in first person as the manager. The tone should be ${toneLabel}. 

Keep it under 250 words. Make it feel real — reference specific details from the context. Use short punchy sentences. No corporate language. No clichés like "at the end of the day" or "give 110%". Think Klopp, Guardiola, Simeone — direct, specific, human.`;

      const userMessage = `Write a ${typeLabel} team talk for ${clubName}.

MATCH: vs ${talkOpp || "our next opponent"}
${talkScore ? `CURRENT SCORE: ${talkScore}` : ""}
MOMENT: ${talkType === "pre" ? "about to kick off" : talkType === "halftime" ? "half-time interval in the dressing room" : "full time whistle just blown"}

OUR CONTEXT:
- Club: ${clubName}
- Formation: ${formation}${systemName ? ` (${systemName})` : ""}
- Recent form (last 5): ${formStr}
${topPerformers ? `- Standout performers last match: ${topPerformers}` : ""}
${lowPerformers ? `- Needed more last match: ${lowPerformers}` : ""}
${principles ? `- Our tactical principles: ${principles}` : ""}
- Squad size: ${squadSize} players

${talkContext ? `MANAGER'S NOTES: ${talkContext}` : ""}

Write the talk as if you are speaking directly to the players in the dressing room. Start immediately — no preamble.`;

      await callClaudeTeamTalk(systemPrompt, userMessage, (text) => setTalkOutput(text));
    } catch(e) {
      setTalkError(`AI Error: ${e.message}`);
    }
    setTalkStreaming(false);
  };

  const TABS = [
    { id:"patterns",  icon:"📈", label:"Pattern Engine" },
    { id:"squad",     icon:"🧠", label:"Squad Analyst" },
    { id:"opp",       icon:"🎯", label:"Opposition Scout" },
    { id:"player",    icon:"👤", label:"Player Dev" },
    { id:"session",   icon:"📅", label:"Session Builder" },
    { id:"teamtalk",  icon:"🗣️",  label:"Team Talk" },
    { id:"video",     icon:"📊", label:"Match Report" },
  ];

  const SQUAD_SUGGESTIONS = [
    "What is our best formation based on this squad?",
    "Where are our biggest weaknesses?",
    "Which players suit a high press system?",
    "Who are our best technical players for a possession system?",
    "What positions do we need to strengthen?",
    "How does our squad compare for a 3-5-2 system?",
  ];

  const PLAYER_SUGGESTIONS = [
    "What is this player's development path over the next 2 years?",
    "What are their biggest improvement areas?",
    "Which tactical system suits them best?",
    "How should I train this player to reach their potential?",
    "What pro player should I model their development on?",
  ];

  const SESSION_SUGGESTIONS = [
    "Improve pressing after losing the ball — high intensity",
    "Work on build-up play from the back against a high press",
    "Set piece — attacking corners with 3 different routines",
    "Defensive shape and compactness in a mid-block",
    "Final third combination play — creating and finishing chances",
    "Transition speed — attack to defence within 3 seconds",
  ];

  return (
    <>
      <style>{AA_CSS}{AI_CSS}</style>
      <div className="aa-wrap">

        {/* HERO */}
        <div className="aa-hero">
          <div className="aa-hero-inner">
            <div>
              <div className="aa-hero-title">✦ AI Football Analyst</div>
              <div className="aa-hero-sub">Powered by Claude. Ask anything about your squad, your next opponent, your players or your training sessions. Real coaching intelligence, not templates.</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:"0.35rem",alignSelf:"flex-end"}}>
              <span className="ai-badge" style={{fontSize:"0.65rem",padding:"0.2rem 0.7rem"}}>Powered by Claude AI</span>
              {squad.length > 0 && <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"rgba(255,255,255,0.5)",fontStyle:"italic"}}>{squad.length} players loaded</span>}
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="aa-tabs">
          {TABS.map(t=>(
            <div key={t.id} className={`aa-tab ${activeTab===t.id?"on":""}`} onClick={()=>setActiveTab(t.id)}>
              <span className="aa-tab-icon">{t.icon}</span>
              {t.label}
            </div>
          ))}
        </div>

        <div className="aa-content">

          {/* ══ PATTERN ENGINE ══ */}
          {activeTab==="patterns"&&(
            <PatternEngineTab
              patternOutput={patternOutput}
              patternStreaming={patternStreaming}
              patternError={patternError}
              patternData={patternData}
              onRun={runPatterns}
              squad={squad}
            />
          )}

          {/* ══ SQUAD ANALYST ══ */}
          {activeTab==="squad"&&(
            <>
              {/* Squad overview */}
              {squad.length > 0 ? (
                <div className="aa-panel">
                  <div className="aa-panel-head">
                    <div className="aa-panel-title">Your Squad — {squad.length} Players</div>
                    <span className="ai-badge">Ready for Analysis</span>
                  </div>
                  <div className="aa-panel-body">
                    <div style={{marginBottom:"0.6rem"}}>
                      {["gk","def","mid","att"].map(grp=>{
                        const count = squad.filter(p=>p.group===grp).length;
                        const pos = grp==="gk"?"GK":grp==="def"?"DEF":grp==="mid"?"MID":"ATT";
                        return count > 0 ? <span key={grp} className={`aa-squad-chip ${grp}`}>{pos}: {count}</span> : null;
                      })}
                    </div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",color:"var(--muted)",fontStyle:"italic"}}>
                      Avg OVR: {Math.round(squad.reduce((s,p)=>s+p.overall,0)/squad.length)} · 
                      Best player: {squad.sort((a,b)=>b.overall-a.overall)[0]?.name} ({squad[0]?.overall})
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aa-panel">
                  <div className="aa-panel-body">
                    <div className="aa-empty">
                      <div className="aa-empty-icon">🏟️</div>
                      <div className="aa-empty-title">No Squad Loaded</div>
                      <div className="aa-empty-sub">Build your squad in Squad Builder first, then come back here for AI analysis.</div>
                      <button onClick={()=>onNavigate&&onNavigate("squadcoach")} style={{marginTop:"1rem",padding:"0.65rem 1.5rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.72rem",letterSpacing:"2px",textTransform:"uppercase",cursor:"pointer"}}>
                        Build Squad →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Question input */}
              <div className="aa-panel">
                <div className="aa-panel-head">
                  <div className="aa-panel-title">Ask the AI Analyst</div>
                </div>
                <div className="aa-panel-body">
                  <label className="aa-label">Your question</label>
                  <textarea className="aa-textarea" placeholder="e.g. What is our best formation? Where are our weaknesses? Which players suit a high press?" value={squadQ} onChange={e=>setSquadQ(e.target.value)}/>
                  <div className="ai-suggestions" style={{marginBottom:"0.75rem"}}>
                    {SQUAD_SUGGESTIONS.map((s,i)=>(
                      <button key={i} className="ai-suggestion" onClick={()=>setSquadQ(s)}>{s}</button>
                    ))}
                  </div>
                  <button className="ai-submit" onClick={runSquadAnalysis} disabled={squadStreaming||!squadQ.trim()||squad.length===0}
                    style={{width:"100%",padding:"0.85rem"}}>
                    {squadStreaming ? "Analysing your squad..." : "✦ Analyse Squad with AI"}
                  </button>
                  {squadStreaming && <div className="ai-loading"><div className="ai-loading-dot"/><div className="ai-loading-dot"/><div className="ai-loading-dot"/><span>Claude is analysing your squad...</span></div>}
                </div>
              </div>

              <AIOutputPanel title="Squad Analysis" output={squadOutput} streaming={squadStreaming} error={squadError} onClear={()=>setSquadOutput("")}/>
            </>
          )}

          {/* ══ OPPOSITION SCOUT ══ */}
          {activeTab==="opp"&&(
            <>
              <div className="aa-panel">
                <div className="aa-panel-head">
                  <div className="aa-panel-title">Opposition Intelligence</div>
                  <span className="ai-badge">AI Generated</span>
                </div>
                <div className="aa-panel-body">
                  <label className="aa-label">Opponent Name *</label>
                  <input className="aa-input" placeholder="e.g. North United, City Academy..." value={oppName} onChange={e=>setOppName(e.target.value)}/>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.75rem"}}>
                    <div>
                      <label className="aa-label">Their Playing Style</label>
                      <select className="aa-select" value={oppStyle} onChange={e=>setOppStyle(e.target.value)}>
                        {["High Press","Low Block","Possession","Direct & Physical","Counter-attack","Mid-block Press","3-5-2 Wing-play","4-4-2 Physical","Tiki-Taka","Route One"].map(s=><option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="aa-label">Their Formation</label>
                      <select className="aa-select" value={oppForm} onChange={e=>setOppForm(e.target.value)}>
                        {["4-3-3","4-4-2","4-2-3-1","3-5-2","3-4-3","5-3-2","4-1-4-1","4-4-2 Diamond","3-4-2-1","4-3-2-1"].map(f=><option key={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                  <label className="aa-label">Known Threats (comma separated, optional)</label>
                  <input className="aa-input" placeholder="e.g. Dangerous set pieces, Pace up front, Strong aerial presence" value={oppThreats} onChange={e=>setOppThreats(e.target.value)}/>
                  <button className="ai-submit" onClick={runOppositionReport} disabled={oppStreaming||!oppName.trim()}
                    style={{width:"100%",padding:"0.85rem",marginTop:"0.75rem"}}>
                    {oppStreaming ? "Generating intelligence report..." : "✦ Generate Tactical Intelligence Report"}
                  </button>
                  {oppStreaming && <div className="ai-loading"><div className="ai-loading-dot"/><div className="ai-loading-dot"/><div className="ai-loading-dot"/><span>Claude is building your opposition dossier...</span></div>}
                </div>
              </div>
              <AIOutputPanel title={`AI Dossier — ${oppName}`} output={oppOutput} streaming={oppStreaming} error={oppError} onClear={()=>setOppOutput("")}/>
            </>
          )}

          {/* ══ PLAYER DEVELOPMENT ══ */}
          {activeTab==="player"&&(
            <>
              <div className="aa-panel">
                <div className="aa-panel-head">
                  <div className="aa-panel-title">Player Development Advisor</div>
                  <span className="ai-badge">Personalised AI</span>
                </div>
                <div className="aa-panel-body">
                  {squad.length === 0 ? (
                    <div className="aa-empty">
                      <div className="aa-empty-icon">👤</div>
                      <div className="aa-empty-title">No Squad Built</div>
                      <div className="aa-empty-sub">Add players to your squad to get personalised AI development advice for each one.</div>
                    </div>
                  ) : (
                    <>
                      <label className="aa-label">Select Player</label>
                      <select className="aa-player-select" value={selPlayerId} onChange={e=>setSelPlayerId(e.target.value)}>
                        <option value="">— Choose a player from your squad —</option>
                        {[...squad].sort((a,b)=>b.overall-a.overall).map(p=>(
                          <option key={p.id} value={p.id}>{p.name} — {p.pos} — OVR {p.overall}</option>
                        ))}
                      </select>

                      {selPlayer && (
                        <div className="aa-player-card">
                          <div className="aa-player-av" style={{background:posGroupColor(selPlayer.group)}}>{ini(selPlayer.name)}</div>
                          <div style={{flex:1}}>
                            <div className="aa-player-name">{selPlayer.name}</div>
                            <div className="aa-player-meta">{selPlayer.pos} · {selPlayer.style} · Age {selPlayer.age} · OVR {selPlayer.overall} → POT {selPlayer.potential}</div>
                            <div className="aa-player-attrs">
                              {[["PAC",selPlayer.pace],["SHO",selPlayer.shooting],["PAS",selPlayer.passing],["DRI",selPlayer.dribbling],["DEF",selPlayer.defending],["PHY",selPlayer.physical]].map(([k,v])=>(
                                <div key={k} className="aa-player-attr" style={{color:v>=80?"#16a34a":v>=65?"var(--mustard)":"var(--darkred)"}}>{k} {v}</div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      <label className="aa-label">Your Question (optional — leave blank for full development plan)</label>
                      <textarea className="aa-textarea" style={{minHeight:70}} placeholder="e.g. How do I improve their passing? What position should they develop into? What drills will help most?" value={playerQ} onChange={e=>setPlayerQ(e.target.value)}/>
                      <div className="ai-suggestions" style={{marginBottom:"0.75rem"}}>
                        {PLAYER_SUGGESTIONS.map((s,i)=>(
                          <button key={i} className="ai-suggestion" onClick={()=>setPlayerQ(s)}>{s}</button>
                        ))}
                      </div>
                      <button className="ai-submit" onClick={runPlayerDev} disabled={playerStreaming||!selPlayer}
                        style={{width:"100%",padding:"0.85rem"}}>
                        {playerStreaming ? "Building development plan..." : `✦ Get AI Development Plan${selPlayer?` for ${selPlayer.name.split(" ")[0]}`:""}`}
                      </button>
                      {playerStreaming && <div className="ai-loading"><div className="ai-loading-dot"/><div className="ai-loading-dot"/><div className="ai-loading-dot"/><span>Claude is building a personalised development plan...</span></div>}
                    </>
                  )}
                </div>
              </div>
              {selPlayer && <AIOutputPanel title={`Development Plan — ${selPlayer.name}`} output={playerOutput} streaming={playerStreaming} error={playerError} onClear={()=>setPlayerOutput("")}/>}
            </>
          )}

          {/* ══ SESSION BUILDER ══ */}
          {activeTab==="session"&&(
            <>
              <div className="aa-panel">
                <div className="aa-panel-head">
                  <div className="aa-panel-title">AI Training Session Builder</div>
                  <span className="ai-badge">Claude Designed</span>
                </div>
                <div className="aa-panel-body">
                  <label className="aa-label">Describe what you want to work on *</label>
                  <textarea className="aa-textarea" placeholder="e.g. We need to improve our pressing. We lost the ball too easily last match and didn't win it back quickly enough. 18 players available, 75 minutes." value={sessionBrief} onChange={e=>setSessionBrief(e.target.value)}/>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"0.6rem"}}>
                    The more detail you give, the better the session. Include: theme, number of players, time available, upcoming match context.
                  </div>
                  <div className="ai-suggestions" style={{marginBottom:"0.75rem"}}>
                    {SESSION_SUGGESTIONS.map((s,i)=>(
                      <button key={i} className="ai-suggestion" onClick={()=>setSessionBrief(s)}>{s}</button>
                    ))}
                  </div>
                  <button className="ai-submit" onClick={runSessionBuilder} disabled={sessionStreaming||!sessionBrief.trim()}
                    style={{width:"100%",padding:"0.85rem"}}>
                    {sessionStreaming ? "Building your session..." : "✦ Build Training Session with AI"}
                  </button>
                  {sessionStreaming && <div className="ai-loading"><div className="ai-loading-dot"/><div className="ai-loading-dot"/><div className="ai-loading-dot"/><span>Claude is designing your training session...</span></div>}
                </div>
              </div>
              <AIOutputPanel title="AI Training Session" output={sessionOutput} streaming={sessionStreaming} error={sessionError} onClear={()=>setSessionOutput("")}/>
            </>
          )}

          {/* ══ VIDEO NARRATIVE ══ */}
          {/* ══ TEAM TALK ══ */}
          {activeTab==="teamtalk"&&(
            <div>
              {/* Type selector */}
              <div style={{display:"flex",gap:0,borderBottom:"2px solid var(--rule)",marginBottom:"1.2rem",background:"var(--white)"}}>
                {[
                  {id:"pre",       label:"⚡ Pre-Match",  desc:"Before kick-off"},
                  {id:"halftime",  label:"🔄 Half-Time",  desc:"In the dressing room"},
                  {id:"post",      label:"✅ Post-Match",  desc:"Full time reaction"},
                ].map(t=>(
                  <button key={t.id} onClick={()=>{setTalkType(t.id);setTalkOutput("");}}
                    style={{flex:1,padding:"0.85rem 0.5rem",background:"transparent",border:"none",borderBottom:`3px solid ${talkType===t.id?"var(--teal)":"transparent"}`,cursor:"pointer",transition:"all 0.15s",textAlign:"center"}}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",fontWeight:800,letterSpacing:"1px",color:talkType===t.id?"var(--teal)":"var(--muted)"}}>{t.label}</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",fontWeight:300}}>{t.desc}</div>
                  </button>
                ))}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.85rem",marginBottom:"0.85rem"}}>
                {/* Opponent */}
                <div>
                  <label style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.35rem"}}>
                    {talkType==="pre"?"Upcoming Opponent":"Opponent"}
                  </label>
                  <input
                    value={talkOpp}
                    onChange={e=>setTalkOpp(e.target.value)}
                    placeholder="e.g. North United"
                    style={{width:"100%",background:"var(--cream)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--ink)",padding:"0.6rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",outline:"none",color:"var(--ink)"}}
                  />
                </div>

                {/* Score — halftime/post only */}
                {(talkType==="halftime"||talkType==="post")&&(
                  <div>
                    <label style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.35rem"}}>
                      Score (Us – Them)
                    </label>
                    <input
                      value={talkScore}
                      onChange={e=>setTalkScore(e.target.value)}
                      placeholder="e.g. 1-2"
                      style={{width:"100%",background:"var(--cream)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--ink)",padding:"0.6rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",outline:"none",color:"var(--ink)"}}
                    />
                  </div>
                )}
              </div>

              {/* Tone selector */}
              <div style={{marginBottom:"0.85rem"}}>
                <label style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.45rem"}}>
                  Tone
                </label>
                <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
                  {[
                    {id:"motivational", label:"Motivational", icon:"🔥"},
                    {id:"calm",         label:"Calm & Clear",  icon:"🧊"},
                    {id:"aggressive",   label:"Demanding",     icon:"⚡"},
                    {id:"tactical",     label:"Tactical",      icon:"🎯"},
                    {id:"honest",       label:"Honest",        icon:"💬"},
                  ].map(t=>(
                    <button key={t.id} onClick={()=>setTalkTone(t.id)}
                      style={{padding:"0.45rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",fontWeight:700,cursor:"pointer",transition:"all 0.15s",
                        border:`1.5px solid ${talkTone===t.id?"var(--teal)":"var(--rule)"}`,
                        background:talkTone===t.id?"var(--teal4)":"var(--white)",
                        color:talkTone===t.id?"var(--teal)":"var(--muted)"}}>
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Extra context */}
              <div style={{marginBottom:"1rem"}}>
                <label style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.35rem"}}>
                  Anything specific to address <span style={{fontWeight:400,textTransform:"none",fontStyle:"italic",letterSpacing:0}}>— optional</span>
                </label>
                <textarea
                  value={talkContext}
                  onChange={e=>setTalkContext(e.target.value)}
                  placeholder={talkType==="pre"
                    ? "e.g. We lost to these 3-0 last time. Our pressing has been poor this week in training. Key player back from injury."
                    : talkType==="halftime"
                    ? "e.g. We're being exposed on the right side. The No.6 needs to sit deeper. We're wasting the ball in midfield."
                    : "e.g. Good result but we gave away too many chances. The second goal was preventable. Set piece delivery was excellent today."
                  }
                  style={{width:"100%",minHeight:75,background:"var(--cream)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--ink)",padding:"0.6rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",fontStyle:"italic",outline:"none",resize:"vertical",color:"var(--ink)",fontWeight:300}}
                />
              </div>

              {/* What Claude will use */}
              <div style={{background:"var(--cream2)",border:"1px solid var(--rule)",borderLeft:"3px solid var(--teal)",padding:"0.65rem 0.9rem",marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",lineHeight:1.65}}>
                <span style={{fontWeight:800,color:"var(--teal)"}}>Claude will read automatically: </span>
                your last 5 results, player ratings from the previous match, your current formation and system, and your club name. No setup needed.
              </div>

              <button
                onClick={runTeamTalk}
                disabled={talkStreaming}
                style={{width:"100%",padding:"0.9rem",background:talkStreaming?"var(--muted)":"var(--darkred)",color:"var(--white)",border:"none",fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"3px",cursor:talkStreaming?"default":"pointer",marginBottom:"1.2rem",transition:"background 0.2s"}}>
                {talkStreaming?"Writing your team talk...":"✦ Generate Team Talk →"}
              </button>

              {talkError&&(
                <div style={{background:"rgba(139,42,42,0.1)",border:"1.5px solid var(--darkred)",borderLeft:"4px solid var(--darkred)",padding:"0.85rem 1rem",marginBottom:"1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--darkred)"}}>
                  {talkError}
                </div>
              )}

              {/* Output */}
              {(talkOutput||talkStreaming)&&(
                <div style={{background:"var(--ink)",border:"2px solid var(--mustard)",padding:"0"}}>
                  <div style={{padding:"0.75rem 1.2rem",borderBottom:"1px solid rgba(255,255,255,0.1)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.85rem",letterSpacing:"2.5px",color:"var(--mustard)"}}>
                      {talkType==="pre"?"Pre-Match Talk":talkType==="halftime"?"Half-Time Talk":"Post-Match Talk"}
                      {talkOpp&&` — vs ${talkOpp}`}
                    </div>
                    {talkOutput&&!talkStreaming&&(
                      <button onClick={()=>{
                        const w=window.open("","_blank","width=700,height=600");
                        w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Team Talk</title><style>body{font-family:Arial,sans-serif;background:#1a1612;color:white;padding:2rem;max-width:600px;margin:0 auto;line-height:1.8;font-size:11pt;}h3{color:#d4941a;letter-spacing:2px;text-transform:uppercase;font-size:9pt;border-bottom:1px solid rgba(255,255,255,0.1);padding-bottom:8px;margin-bottom:16px;}p{margin-bottom:12px;}button{position:fixed;top:16px;right:16px;padding:10px 20px;background:#2e7d7a;color:white;border:none;cursor:pointer;font-weight:800;}</style></head><body><h3>${talkType==="pre"?"Pre-Match":talkType==="halftime"?"Half-Time":"Post-Match"} Team Talk${talkOpp?` — vs ${talkOpp}`:""}</h3><p>${talkOutput.replace(/\n/g,"</p><p>")}</p><button onclick="window.print()">🖨 Print</button></body></html>`);
                        w.document.close();
                      }}
                        style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:700,background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.5)",padding:"0.25rem 0.6rem",cursor:"pointer"}}>
                        🖨 Print
                      </button>
                    )}
                  </div>
                  <div style={{padding:"1.5rem 1.8rem"}}>
                    <AIResponse text={talkOutput} streaming={talkStreaming}/>
                  </div>
                </div>
              )}

              {/* Quick re-generate in different tone */}
              {talkOutput&&!talkStreaming&&(
                <div style={{marginTop:"0.75rem",display:"flex",gap:"0.4rem",flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>Try again in a different tone:</span>
                  {["motivational","calm","aggressive","tactical","honest"].filter(t=>t!==talkTone).map(t=>(
                    <button key={t} onClick={()=>{setTalkTone(t);setTimeout(runTeamTalk,50);}}
                      style={{padding:"0.25rem 0.65rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:700,background:"transparent",border:"1px solid var(--rule)",color:"var(--muted)",cursor:"pointer"}}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab==="video"&&(
            <>
              {savedAnalyses.length === 0 ? (
                <div className="aa-panel">
                  <div className="aa-panel-body">
                    <div className="aa-empty">
                      <div className="aa-empty-icon">📊</div>
                      <div className="aa-empty-title">No Match Analyses Yet</div>
                      <div className="aa-empty-sub">Log your first match stats in Match Analysis, then come back here to get a Claude-written coaching report interpreting your real data.</div>
                      <button onClick={()=>onNavigate&&onNavigate("video")} style={{marginTop:"1rem",padding:"0.65rem 1.5rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.72rem",letterSpacing:"2px",textTransform:"uppercase",cursor:"pointer"}}>
                        Log Match Stats →
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="aa-panel">
                    <div className="aa-panel-head">
                      <div className="aa-panel-title">AI Match Report Writer</div>
                      <span className="ai-badge">Claude Analyst</span>
                    </div>
                    <div className="aa-panel-body">
                      <label className="aa-label">Select Match Analysis</label>
                      <select className="aa-select" value={selAnalysis} onChange={e=>setSelAnalysis(Number(e.target.value))} style={{marginBottom:"0.85rem"}}>
                        {savedAnalyses.map((a,i)=>(
                          <option key={i} value={i}>
                            {a.matchData?.opponent||"Match"} — {a.matchData?.result||"Result TBC"} · Press: {a.pressEfficiency}%
                          </option>
                        ))}
                      </select>

                      {latestAnalysis && (
                        <div className="aa-video-row">
                          <span className="aa-video-chip">Press <strong>{latestAnalysis.pressEfficiency}%</strong></span>
                          <span className="aa-video-chip">Poss <strong>{latestAnalysis.possessionPct}%</strong></span>
                          <span className="aa-video-chip">Pass <strong>{latestAnalysis.passAccuracy}%</strong></span>
                          <span className="aa-video-chip">Chances <strong>{latestAnalysis.chancesCreated}</strong></span>
                          <span className="aa-video-chip">Press Wins <strong>{latestAnalysis.highPressWins}</strong></span>
                          <span className="aa-video-chip">Distance <strong>{latestAnalysis.distanceCovered}km</strong></span>
                        </div>
                      )}

                      <button className="ai-submit" onClick={runVideoNarrative} disabled={videoStreaming}
                        style={{width:"100%",padding:"0.85rem"}}>
                        {videoStreaming ? "Writing match report..." : "✦ Generate AI Match Report"}
                      </button>
                      {videoStreaming && <div className="ai-loading"><div className="ai-loading-dot"/><div className="ai-loading-dot"/><div className="ai-loading-dot"/><span>Claude is writing your match analysis report...</span></div>}
                    </div>
                  </div>
                  <AIOutputPanel title="AI Match Report" output={videoOutput} streaming={videoStreaming} error={videoError} onClear={()=>setVideoOutput("")}/>
                </>
              )}
            </>
          )}

        </div>
      </div>
    </>
  );
}
